/** Backend CHECK constrains to days|weeks only. */
export type PostAppointmentOffsetUnit = "days" | "weeks";

export type PostAppointmentAttachmentSourceType =
  | "template"
  | "salon_template"
  | "salon_document";

export interface PostAppointmentAttachment {
  uuid: string;
  source_type: PostAppointmentAttachmentSourceType;
  source_id: string;
  sort_order: number;
}

export interface PostAppointmentMessage {
  uuid: string;
  offset_value: number;
  offset_unit: PostAppointmentOffsetUnit;
  label: string | null;
  sort_order: number;
  attachments: PostAppointmentAttachment[];
  /** Stable sort key so 7d == 1w. */
  offset_minutes: number;
}

export interface PostAppointmentSettings {
  master_enabled: boolean;
  messages: PostAppointmentMessage[];
}

export const DEFAULT_POST_APPOINTMENT_SETTINGS: PostAppointmentSettings = {
  master_enabled: false,
  messages: [],
};

export interface GetPostAppointmentSettingsResponse {
  post_appointment_settings: PostAppointmentSettings;
}

/** Missing keys = no-change. */
export interface UpdatePostAppointmentSettingsPayload {
  master_enabled?: boolean;
}

export interface PostAppointmentAttachmentPayload {
  source_type: PostAppointmentAttachmentSourceType;
  source_id: string;
  sort_order?: number;
}

export interface CreatePostAppointmentMessagePayload {
  offset_value: number;
  offset_unit: PostAppointmentOffsetUnit;
  label?: string | null;
  sort_order?: number;
  attachments?: PostAppointmentAttachmentPayload[];
}

/** `attachments` omitted = leave existing list untouched; pass [] to clear. */
export interface UpdatePostAppointmentMessagePayload {
  offset_value?: number;
  offset_unit?: PostAppointmentOffsetUnit;
  label?: string | null;
  sort_order?: number;
  attachments?: PostAppointmentAttachmentPayload[];
}

/** Wizard pre-save shape; replayed as POSTs once the service exists. */
export interface PendingPostAppointmentAttachment {
  tempId: string;
  source_type: PostAppointmentAttachmentSourceType;
  source_id: string;
  /** Display only; not sent to backend. */
  name: string;
  sort_order: number;
}

export interface PendingPostAppointmentMessage {
  tempId: string;
  offset_value: number;
  offset_unit: PostAppointmentOffsetUnit;
  label: string | null;
  sort_order: number;
  attachments: PendingPostAppointmentAttachment[];
}

export function computeOffsetMinutes(
  value: number,
  unit: PostAppointmentOffsetUnit,
): number {
  const perUnit = unit === "weeks" ? 60 * 24 * 7 : 60 * 24;
  return value * perUnit;
}

export function describeOffset(
  value: number,
  unit: PostAppointmentOffsetUnit,
): string {
  const singular = unit === "weeks" ? "week" : "day";
  const noun = value === 1 ? singular : `${singular}s`;
  return `${value} ${noun} after appointment`;
}
